import { Component, ContentChild, Input, Output, TemplateRef, EventEmitter, ViewChild, OnDestroy, NgZone, OnInit, Injector, QueryList } from '@angular/core';
import { ViewportRuler } from '@angular/cdk/scrolling';
import { BehaviorSubject, Observable, Subscription } from 'rxjs';

import { SplitterComponent } from '@syncfusion/ej2-angular-layouts';

//import { AuthorizeService } from 'src/external-modules/api-authorization/authorize.service';
import { CerSessionService } from '../cer-session/cer-session.service';
import { CerAppRouteService } from 'src/cer-app/cer-app-route/cer-app-route.service';

import { ICerViewPersistenceComponent, CerMetaUserView, CerFormPersistenceService } from './cer-form-persistence.service';
import { UiCommandEvent, UiActionTypeEnum } from '@ui/cer-ui-types';
import { CerFormService } from './cer-form.service';
import { CerFormPanesService, CerFormPanesSettings } from './cer-form-panes/cer-form-panes.service';
import { CerFormNavigationService } from '@form/cer-form-navigation.service';
import { CerFormContextService } from '@form/cer-form-context.service';
import { CerFormPaneComponent } from '@form/cer-form-panes/cer-form-pane/cer-form-pane.component';

export type CerFormActiveDisabled = 'active' | 'disabled';
export type CerFormType = 'standard' | 'dialog';
export type CerFromInfoAction = 'list' | 'detail' | 'select';

export interface ICerFormOwner {
  formRecreate(): void;
}

export interface ISubFormComponent {
}

@Component({
  selector: 'cer-form',
  templateUrl: './cer-form.component.html',
  styleUrls: ['./cer-form.component.css'],
  providers: [CerFormService, CerFormPersistenceService, CerFormPanesService,
    CerFormNavigationService, CerFormContextService]
})
export class CerFormComponent implements OnInit, OnDestroy, ICerViewPersistenceComponent {
  @Input() routerActive: CerFormActiveDisabled = 'active';
  @Input() persistence: CerFormActiveDisabled = 'active';
  @Input() formType: CerFormType = 'standard';
  @Input() infoForm: boolean = false;
  @Input() infoAction: CerFromInfoAction = null;
  @Input() panesDynamic: boolean = false;
  get panes(): CerFormPanesSettings { return this.panesService.panesGet(); };
  @Input() set panes(value: CerFormPanesSettings) { this.panesService.panesInit(value) };
  @ContentChild('mainTemplate') mainTemplate: TemplateRef<any>;
  @ContentChild('leftTemplate') leftTemplate?: TemplateRef<any>;
  @ContentChild('rightTemplate') rightTemplate?: TemplateRef<any>;
  @ContentChild('infoTemplate') infoTemplate?: TemplateRef<any>;
  @ContentChild('topTemplate') topTemplate?: TemplateRef<any>;
  @ContentChild('bottomTemplate') bottomTemplate?: TemplateRef<any>;
  @ContentChild('bottomLeftTemplate') bottomLeftTemplate?: TemplateRef<any>;
  @ContentChild('bottomRightTemplate') bottomRightTemplate?: TemplateRef<any>;
  @ContentChild('popupTemplate') popupTemplate?: TemplateRef<any>;
  @ContentChild('popupTemplateContext') popupTemplateContext?: any;
  @ContentChild('dialogFooter') dialogFooterTemplate?: TemplateRef<any>;
  @ContentChild('dialogFooterContext') dialogFooterTemplateContext?: any;
  @Output() commmand: EventEmitter<UiCommandEvent> = new EventEmitter<UiCommandEvent>();

  protected isActive$: BehaviorSubject<boolean> = new BehaviorSubject<boolean>(false);
  protected isReady$: BehaviorSubject<boolean> = new BehaviorSubject<boolean>(false);
  public popupComponent$: Observable<ISubFormComponent>;
  public popupComponentContext$: Observable<any>;
  public infoComponent$: Observable<ISubFormComponent>;
  public infoComponentContext$: Observable<any>;

  public owner : ICerFormOwner;
  public recreate() {
    this.owner?.formRecreate();
  }

  private onFormCommand(event: UiCommandEvent) {
    //this.cdr.detectChanges();
    this.commmand.emit(event);
  }

  public paneComponents: QueryList<CerFormPaneComponent>;
  public initContext(paneComponents: QueryList<CerFormPaneComponent>) {
    if (this.panesDynamic && this.panes == null ) {
      //console.log("Awaiting panes setup on form..");
      return;
    }
    this.paneComponents = paneComponents;
    this.formContextService.initFromForm(this);
  }

  protected paneTopVisible: boolean = false;
  protected paneMainVisible: boolean = false;
  protected paneLeftVisible: boolean = false;
  protected paneRightVisible: boolean = false;
  protected paneRightBottomVisible: boolean = false;
  protected paneInfoVisible: boolean = false;
  protected paneBottomVisible: boolean = false;
  protected paneBottomLeftVisible: boolean = false;
  protected paneBottomRightVisible: boolean = false;
  protected splitterHorizontalVisible: boolean = false;
  protected splitterBottomHorizontalVisible: boolean = false;
  protected splitterVerticalVisible: boolean = false;
  protected splitterRightVerticalVisible: boolean = false;
  protected splitterVerticalPriority: boolean = false;

  protected paneTopCollapsed: boolean = false;
  protected paneLeftCollapsed: boolean = false;
  protected paneRightCollapsed: boolean = false;
  protected paneInfoCollapsed: boolean = false;
  protected paneBottomCollapsed: boolean = false;
  protected paneBottomLeftCollapsed: boolean = false;
  protected paneBottomRightCollapsed: boolean = false;

  protected paneTopCollapsible: boolean = false;
  protected paneLeftCollapsible: boolean = false;
  protected paneRightCollapsible: boolean = false;
  protected paneInfoCollapsible: boolean = false;
  protected paneBottomCollapsible: boolean = false;
  protected paneBottomLeftCollapsible: boolean = false;
  protected paneBottomRightCollapsible: boolean = false;

  protected paneTopSize: string;
  protected paneMainVerticalSize: string;
  protected paneBottomSize: string;

  protected paneLeftSize: string;
  protected paneMainHorizontalSize: string;
  protected paneRightSize: string;
  protected paneRightVerticalSize: string;
  protected paneInfoSize: string;

  protected paneBottomLeftSize: string;
  protected paneBottomHorizontalSize: string;
  protected paneBottomRightSize: string;

  public onPanesChanged(panes: CerFormPanesSettings): void {
    if (panes != null) {
      var primaryVertical: boolean = panes.orientationPrimary === 'vertical';

      this.paneTopVisible = panes.top ? panes.top.visible != 'hide' : false;
      this.paneMainVisible = panes.main ? panes.main.visible != 'hide' : false;
      this.paneBottomVisible = panes.bottom ? panes.bottom.visible != 'hide' : false;
      this.paneLeftVisible = panes.left ? panes.left.visible != 'hide' : false;
      this.paneRightVisible = panes.right ? panes.right.visible != 'hide' : false;
      this.paneInfoVisible = panes.info ? panes.info.visible != 'hide' : false;
      this.paneBottomLeftVisible = panes.bottomLeft ? panes.bottomLeft.visible != 'hide' : false;
      this.paneBottomRightVisible = panes.bottomRight && primaryVertical ? panes.bottomRight.visible != 'hide' : false;
      this.paneRightBottomVisible = panes.bottomRight && !primaryVertical ? panes.bottomRight.visible != 'hide' : false;

      this.splitterVerticalPriority = primaryVertical;
      this.splitterHorizontalVisible = this.paneLeftVisible || this.paneRightVisible || this.paneInfoVisible;
      this.splitterBottomHorizontalVisible = (this.paneBottomLeftVisible || this.paneBottomRightVisible) && primaryVertical;
      this.splitterRightVerticalVisible = this.paneRightBottomVisible && !primaryVertical;
      this.splitterVerticalVisible = this.paneTopVisible || this.paneBottomVisible;

      this.paneTopCollapsed = panes.top ? panes.top.visible != 'show' : true;
      this.paneLeftCollapsed = panes.left ? panes.left.visible != 'show' : true;
      this.paneRightCollapsed = panes.right ? panes.right.visible != 'show' : true;
      this.paneInfoCollapsed = panes.info ? panes.info.visible != 'show' : true;
      this.paneBottomCollapsed = panes.bottom ? panes.bottom.visible != 'show' : true;
      this.paneBottomLeftCollapsed = panes.bottomLeft ? panes.bottomLeft.visible != 'show' : true;
      this.paneBottomRightCollapsed = panes.bottomRight ? panes.bottomRight.visible != 'show' : true;

      this.paneTopCollapsible = panes.top ? panes.top.visibleWhenClose != 'show' : true;
      this.paneLeftCollapsible = panes.left ? panes.left.visibleWhenClose != 'show' : true;
      this.paneRightCollapsible = panes.right ? panes.right.visibleWhenClose != 'show' : true;
      this.paneInfoCollapsible = panes.info ? panes.info.visibleWhenClose != 'show' : true;
      this.paneBottomCollapsible = panes.bottom ? panes.bottom.visibleWhenClose != 'show' : true;
      this.paneBottomLeftCollapsible = panes.bottomLeft ? panes.bottomLeft.visibleWhenClose != 'show' : true;
      this.paneBottomRightCollapsible = panes.bottomRight ? panes.bottomRight.visibleWhenClose != 'show' : true;

      this.paneTopSize = panes.top ? panes.top.size : '25%';
      var sizeMainV: string = panes.main ? primaryVertical ? panes.main.size : panes.main.sizeSecondary : '';
      this.paneMainVerticalSize = (!sizeMainV || sizeMainV.length === 0) ? '75%' : sizeMainV;
      this.paneBottomSize = panes.bottom ? panes.bottom.size : '25%';

      this.paneLeftSize = panes.left ? panes.left.size : '25%';
      var sizeMainH: string = panes.main ? !primaryVertical ? panes.main.size : panes.main.sizeSecondary : '';
      this.paneMainHorizontalSize = (!sizeMainH || sizeMainH.length === 0) ? '75%' : sizeMainH;
      this.paneRightSize = panes.right ? panes.right.size : '25%';
      this.paneRightVerticalSize = panes.right?.sizeSecondary ?? '75%';
      this.paneInfoSize = panes.info ? panes.info.size : '30%';

      this.paneBottomLeftSize = panes.bottomLeft?.size ?? '25%';
      this.paneBottomHorizontalSize = panes.bottom?.sizeSecondary ?? '75%';
      this.paneBottomRightSize = panes.bottomRight?.size ?? '25%';
    }
    this.isReady$.next(true);
  }

  protected dialogFooterTemplateDefault: TemplateRef<any>;
  @ViewChild('dialogFooterTemplateDefault', { static: true }) set setDialogFooterTemplateDefault(template: TemplateRef<any>) {
    this.dialogFooterTemplateDefault = template;
    if (this.dialogFooterTemplate == null) {
      this.dialogFooterTemplate = this.dialogFooterTemplateDefault;
    }
  }

  // Horizontal splitter used in panes service
  public hSplitter: SplitterComponent;
  @ViewChild('hSplitter') set setHSplitter(hSplitter: SplitterComponent) {
    if (hSplitter) {
      this.hSplitter = hSplitter;
      this.panesService.hSplitterLink(hSplitter, this.panes);
    }
  }

  public hbSplitter: SplitterComponent;
  @ViewChild('hbSplitter') set setHBSplitter(hbSplitter: SplitterComponent) {
    if (hbSplitter) {
      this.hbSplitter = hbSplitter;
      this.panesService.hSplitterLink(hbSplitter, this.panes);
    }
  }

  // Vertical splitter 
  public vSplitter: SplitterComponent;
  @ViewChild('vSplitter') set setvSplitter(vSplitter: SplitterComponent) {
    if (vSplitter) {
      this.vSplitter = vSplitter;
      this.panesService.vSplitterLink(vSplitter, this.panes);
    }
  }

  private subscriptionManager$: Subscription = new Subscription();

  private persistenceService: CerFormPersistenceService
  constructor(
    public formContextService: CerFormContextService,
    public formService: CerFormService,
    public formNavigationService: CerFormNavigationService,
    public panesService: CerFormPanesService,
    private viewportRuler: ViewportRuler,
    private ngZone: NgZone,
    private routeService: CerAppRouteService,
    private sessionService: CerSessionService,
    private injector: Injector) {

    this.persistenceService = this.injector.get(CerFormPersistenceService, null, { optional: true }); // If constructed in alternative context
    this.manage(this.formService.formCommand$.subscribe(event => this.onFormCommand(event)));
    this.manage(this.panesService.panesChanged$.subscribe(panes => this.onPanesChanged(panes)));
  }

  ngOnInit(): void {
    this.routerSetup();
    this.viewPortSetup();
    this.persistenceSetup();
  }

  public subscriptionManager: Subscription = new Subscription();
  private manage(s: Subscription) {
    this.subscriptionManager.add(s);
  }

  ngOnDestroy() {
    this.subscriptionManager.unsubscribe();
  }

  private routerSetup() {
    this.subscriptionManager$.add(
      this.sessionService.sessionState$.subscribe(state => {
        const isActive : boolean = state == 'active';
        this.isActive$.next(isActive);
        if (this.routerActive === 'active') {
          this.routeService.setIsLoading(isActive);
        }
      })
    );
  }

  private viewPortSetup() {
    this.subscriptionManager$.add(
      this.viewportRuler.change(200).subscribe(() => this.ngZone.run(() => this.onViewPortChange()))
    );
  }

  private onViewPortChange() {
    this.formService.formDesignChangeNext(UiActionTypeEnum.ViewPort);
  }

  // Dialog
  protected formValid() {
    return true;
  }
  
  protected onDialogClose() {
    //this.formService.formCommand$.next('closeDialog');
  }

  // Persistence
  private persistenceSetup() {
    if (this.persistence === 'active' && this.persistenceService != null) {
      this.persistenceService.registerComponent(this);
      this.persistenceService.linkRouter();
    }
  }

  public getPersistenceId(): string {
    return 'form';
  }

  public getPersistence(): any {
    var o: any = new Object();
    o['panes'] = this.panesService.getPersistence();
    return o;
  }

  public applyPersistence(view: CerMetaUserView, o: any): boolean {
    return this.panesService.applyPersistence(o ? o['panes'] : null);
  }

  public applyPersistenceComplete(view: CerMetaUserView) {
  }
}
